// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import L from 'leaflet';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import {
    defineMessages, FormattedMessage, injectIntl,
} from 'react-intl';
import { Formik } from 'formik';
import {
    get, isEmpty, isEqual, omit,
} from 'lodash';
import type { IntlShape } from 'react-intl';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { connectAPI } from 'speed-js-react';
import { MessageDialog } from 'speed-js-react-web';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import Creator from 'esm-web-pro/components/maps/Creator';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import type { Resource } from 'esm-web-pro/containers/map/types';
import PointForm from 'esm-web-pro/containers/pages/network/PointForm';
import PipeForm from 'esm-web-pro/containers/pages/network/PipeForm';
import NetworkDrawForm from 'esm-web-pro/containers/pages/network/NetworkDrawForm';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import {
    pointIcon,
    canConnect,
    numberedPointIcon,
    determineProviderColor,
    isPointSnappedToLine,
    getSegmentBetweenPoints,
} from 'esm-web-pro/containers/pages/network/Util';
import UseHighlightLine from 'esm-web-pro/hooks/useHighlightLine';
import useHighlightCable from 'esm-web-pro/containers/pages/network/objects/cable/useHighlightCable';
import { pointsHighlightCallback } from 'esm-web-pro/containers/pages/network/highlight';
import type { UUID } from 'esm-web-pro/types/common';
import CreateCable from 'esm-web-pro/containers/pages/network/objects/cable/create/CreateCable';
import { v4 as uuidv4 } from 'uuid';
import { POINTS_MIN_ZOOM } from 'esm-web-pro/containers/pages/network/NetworkTab';
import { Box } from 'grommet';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';


type SidePanelActionProps = {
    openPanel: (key: string, url: ?string) => { key: string, url: string },
    closePanel: (key: string) => { key: string },
    closeTopmost: () => {},
    closeAllPanels: () => {},
    setPanelSensitivity: (key: string, sensitivity: boolean,
                          confirmMessage: string) => { key: string, sensitivity: boolean, confirmMessage: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    id: string,
    context: { uuid: string, identity: string },
    resources: Array<Resource>,
    onClose?: Function,
    drawingFeatureGroup: { current: null | (React.Node & { leafletElement: Object }) },
    leaflet: LeafletContext,
    createObject: (resourceName: string, map: string, data: Object) => Promise<Object>,
    validatePoint: (resourceName: string, map: string, data: Object) => Promise<Object>,
    linesToDetermineProvider: (map: UUID, gisLine: UUID) => Promise<Object>,
    drawing: boolean,
    intl: IntlShape,
    editor: BaseEditor,
}

type PointData = {
    points: Array<Object>,
}

type LineData = {
    points: Array<Object>,
    lines: Array<Object>,
    coordinates: Array<[number, number]>,
    lineColors: Object,
    addedPoints: Array<Object>, // points added while drawing the line (which still do not exist in the backend)
}

type DrawingObject = {
    provider: ?{ id: string, entity: { name: string }, color: string },
    object: ?{ model: string },
    gisResource: ?Resource,
    resource: ?Object,
    data: ?(PointData | LineData), // TODO: Move to specific component
    formData: Object,
    modelData?: Object,
}

type State = {
    drawingObject: DrawingObject,
    canDraw: boolean,
    countCreatedObjects: number,
    closeMsgDialog: boolean,
    submitting: boolean,
}

const DRAWING_RESOURCES = {
    shaft: {
        resource: {
            name: 'shaft.geo',
            excludeFields: ['infrastructure_provider', 'coordinates', 'stage', 'alias'],
            excludePropertyOwnerFields: ['property_owner'],
            defaultProviderOnlyFields: ['export'],
        },
        gisResourceName: 'gisPoint.geo',
    },
    pillar: {
        resource: {
            name: 'pillar.geo',
            excludeFields: ['infrastructure_provider', 'coordinates', 'stage', 'alias'],
            excludePropertyOwnerFields: ['property_owner'],
            defaultProviderOnlyFields: ['export'],
        },
        gisResourceName: 'gisPoint.geo',
    },
    cabinet: {
        resource: {
            name: 'cabinet.geo',
            excludeFields: ['infrastructure_provider', 'coordinates', 'stage'],
            excludePropertyOwnerFields: ['property_owner'],
            defaultProviderOnlyFields: ['export'],
        },
        gisResourceName: 'gisPoint.geo',
    },
    wallpoint: {
        resource: {
            name: 'wallpoint.geo',
            excludeFields: ['infrastructure_provider', 'coordinates', 'stage', 'alias'],
            excludePropertyOwnerFields: ['property_owner'],
            defaultProviderOnlyFields: ['export'],
        },
        gisResourceName: 'gisPoint.geo',
    },
    cable: {
        resource: {
            name: 'cable.geo',
            excludeFields: ['infrastructure_provider', 'path', 'start_termination_point', 'end_termination_point',
                'length', 'stage', 'export', 'alias'],
            defaultProviderOnlyFields: ['export'],
        },
        gisResourceName: 'gisLine.geo',
    },
    pipe: {
        resource: {
            name: 'pipe.geo',
            excludeFields: ['infrastructure_provider', 'path', 'start_point', 'end_point', 'gis_line', 'is_virtual',
                'stage', 'provider_ref', 'export', 'alias'],
            excludePropertyOwnerFields: ['property_owner'],
            defaultProviderOnlyFields: ['export'],
        },
        gisResourceName: 'gisLine.geo',
    },
    gispoint: {
        resource: {
            name: 'gisPoint.geo',
            excludeFields: ['infrastructure_provider', 'coordinates', 'alias', 'stage', 'description'],
            defaultProviderOnlyFields: [],
        },
        gisResourceName: 'gisPoint.geo',
    },
    pop: {
        resource: {
            name: 'pop.geo',
            excludeFields: ['infrastructure_provider', 'coordinates', 'stage'],
            excludePropertyOwnerFields: ['property_owner'],
            defaultProviderOnlyFields: ['export'],
        },
        gisResourceName: 'gisPoint.geo',
    },
    linecut: {
        resource: {
            name: 'linecut.geo',
            excludeFields: ['infrastructure_provider', 'coordinates', 'stage', 'provider_ref', 'alias'],
            excludePropertyOwnerFields: ['property_owner'],
            defaultProviderOnlyFields: ['export'],
        },
        gisResourceName: 'gisPoint.geo',
    },
    fork: {
        resource: {
            name: 'fork.geo',
            excludeFields: ['infrastructure_provider', 'coordinates', 'stage', 'provider_ref', 'alias'],
            defaultProviderOnlyFields: ['export'],
        },
        gisResourceName: 'gisPoint.geo',
    },
};

const messages = defineMessages({
    error: {
        id: 'cancel-btn',
        defaultMessage: 'Cancel',
    },
    errorTitle: {
        id: 'error-title',
        defaultMessage: 'Error',
    },
    restrictZoom: {
        id: 'restrict-zoom',
        defaultMessage: 'The drawing is disabled under zoom level {minZoom}',
    },
});

const LINE_FORM_KEY = 'LINE_FORM_KEY';
const PIPE_FORM_KEY = 'PIPE_FORM_KEY';
const POINT_FORM_KEY = 'POINT_FORM_KEY';

const MAX_POINT_OBJECTS_COUNT = 20;
const MAX_POINT_POP_COUNT = 1;
const MAX_POINT_LINE_CUT_COUNT = 1;
const MAX_LINE_OBJECTS_COUNT = 20;
const MAX_CABLE_LINE_OBJECTS_COUNT = 50;

const LINE_CUT_MODEL = 'linecut';
const FORK_MODEL = 'fork';
const SNAPPABLE_MODELS = [LINE_CUT_MODEL, FORK_MODEL];

const mapDispatchToProps = (dispatch) => (bindActionCreators(sidePanelActions, dispatch));

@withLeaflet
@connectAPI((apiClient: ApiClient) => ({
    createObject: {
        send: (resourceName, map, data) => apiClient.resources[resourceName].create(map, data),
    },
    validatePoint: {
        send: (resourceName, map, data) => apiClient.resources[resourceName].validatePoint(map, data),
    },
    linesToDetermineProvider: {
        send: (map, gisLine) => apiClient.resources['gisLine.geo'].linesToDetermineProvider(map, gisLine),
    },
}))
@connect(null, mapDispatchToProps)
class NetworkCreator extends React.Component<Props, State> {
    formikRef: React.ElementRef<Formik>;

    static defaultProps = {
        leaflet: {},
        onClose: undefined,
    };

    forkSnappedToGISLine = false;

    constructor(props: Props) {
        super(props);
        this.state = {
            drawingObject: {
                provider: null,
                object: null,
                gisResource: null,
                resource: null,
                data: null,
            },
            canDraw: false,
            countCreatedObjects: 1,
            nonFieldErrData: null,
            closeMsgDialog: false,
            submitting: false,
        };
        this.formikRef = React.createRef();
    }

    componentWillUnmount() {
        this.handleStopDrawing();
    }

    /**
     * Checks which objects with which other objects can be connected/drawn
     *
     * @param {Object} layer The layer that is clicked
     * @return {void}
     */
    registerPointClickHandler = () => {
        const disabledPoints = [];
        const pointsWithClickHandlers = [];
        return {
            run: (layer) => {
                const { drawingObject: { object, data, gisResource } } = this.state;
                const { drawing } = this.props;
                const drawingLine = gisResource && gisResource.name === 'gisLine.geo';
                const layerIsPoint = layer.feature && layer.feature.geometry.type === 'Point'
                    && layer.options.icon.options.className !== 'address-custom-number';

                if (!drawing || !object || !drawingLine || !layerIsPoint
                    || (data.points && data.points.includes(layer))) return;

                const lastPointLayer = (data && data.points) ? data.points[data.points.length - 1] : null;
                const newPoint = layer.feature.properties.point;
                const undefinedPoint = !newPoint;
                const firstIsFork = data.points.length === 0 && newPoint?.type === 'fork';

                const noLastPointOrCanConnect = !lastPointLayer
                    || canConnect(lastPointLayer.feature.properties.point, newPoint);
                const canClickIfCable = object.model === 'cable' && noLastPointOrCanConnect
                    && !undefinedPoint && !firstIsFork;

                const withUndergroundConnection = newPoint?.child?.underground_connection
                    || undefinedPoint;

                const canClickIfPipe = object.model === 'pipe' && withUndergroundConnection && !firstIsFork;
                const maxCount = object.model === 'cable' ? MAX_CABLE_LINE_OBJECTS_COUNT : MAX_LINE_OBJECTS_COUNT;

                // If we have nothing selected and we're drawing a pipe we want to disable the fork as a starting point
                // but allow it once there is a feature selected to act as a middle point
                const canClickForkIfPipe = object.model === 'pipe'
                    && lastPointLayer === undefined
                    && layer.feature.properties.point?.child.type === 'fork';

                if (
                    (canClickIfCable || canClickIfPipe)
                    && !canClickForkIfPipe
                    && data.points
                    && data.points.length <= maxCount
                ) {
                    const enabledIcon = newPoint ? pointIcon(newPoint.child?.type, false, newPoint.color,
                        newPoint.child?.underground_connection, newPoint.content_count)
                        : pointIcon(newPoint, false);
                    layer.on('click', this.handleCreatedLine);
                    layer.setIcon(enabledIcon);
                    pointsWithClickHandlers.push(layer);
                } else {
                    const disabledIcon = newPoint ? pointIcon(newPoint.child?.type, true, newPoint.color,
                        newPoint.child?.underground_connection, newPoint.content_count)
                        : pointIcon(newPoint, true);
                    layer.off('click', this.handleCreatedLine);
                    layer.setIcon(disabledIcon);
                    disabledPoints.push(layer);
                }
            },

            cleanUp: () => {
                disabledPoints.forEach((layer) => {
                    const { feature: { properties: { point } } } = layer;
                    let icon;
                    if (point) {
                        icon = pointIcon(point.child.type, false,
                            point.color, point.child.underground_connection, point.content_count);
                    } else {
                        icon = pointIcon('', false);
                    }
                    layer.setIcon(icon);
                });

                pointsWithClickHandlers.forEach((layer) => {
                    layer.off('click', this.handleCreatedLine);
                });
            },
        };
    };

    pipeWithForksDrawHandler = () => {
        const { leaflet: { map }, context } = this.props;
        const { drawingObject: { provider } } = this.state;
        const enabledForkForProvider = provider.allowed_objects.find(
            (a) => a.content_types.find((ct) => ct.model === 'fork') && a.map === context.identity,
        );

        if (!enabledForkForProvider) return;

        map.pm.Draw.Marker.enable({
            markerStyle: {
                icon: pointIcon(
                    'fork', false, provider.color, true,
                ),
                draggable: false,
                opacity: 0,
            },
            snappable: true,
            tooltips: false,
        });

        this.forkSnappedToGISLine = null;

        map.pm.Draw.Marker._layer.on('pm:snap', ({
            marker, snapLatLng, layerInteractedWith,
        }) => {
            const { drawingObject: { data } } = this.state;
            const key = `${snapLatLng.lng}-${snapLatLng.lat}`;
            if (!get(map, ['coordsToLayers', key, 'point'])
                && layerInteractedWith.feature?.geometry.type === 'LineString'
                && data.points.length
                && !data.lines.some((line) => isPointSnappedToLine(map, snapLatLng, line.getLatLngs()))
            ) {
                marker.setOpacity(1);
                this.forkSnappedToGISLine = layerInteractedWith;
            } else {
                marker.setOpacity(0);
                this.forkSnappedToGISLine = null;
            }
        });

        map.pm.Draw.Marker._layer.on('pm:unsnap', ({
            marker,
        }) => {
            this.forkSnappedToGISLine = null;
            marker.setOpacity(0);
        });

        map.on('pm:create', (e) => {
            const { drawingObject: { data }, drawingObject } = this.state;
            if (!this.checkOverlapping(e) && this.forkSnappedToGISLine) {
                const pointLayerOnMap = this.drawPoint(e.layer, { model: 'fork' },
                    data.points.length + 1, drawingObject);
                pointLayerOnMap.isNew = true;
                this.setState((prevState) => ({
                    drawingObject:
                        {
                            ...prevState.drawingObject,
                            data: {
                                ...prevState.drawingObject.data,
                                addedPoints: prevState.drawingObject.data.addedPoints.concat([pointLayerOnMap]),
                            },
                        },
                }));
                e.layer.remove();
                this.handleCreatedLine({ target: pointLayerOnMap }, this.forkSnappedToGISLine);
            }
        });
    };

    handleLoadModelData = (modelData) => {
        this.setState((prevState) => ({
            ...prevState,
            drawingObject: {
                ...prevState.drawingObject,
                modelData,
            },
        }));
    };

    handleProviderChange = (provider: Object) => {
        this.setState({
            drawingObject: {
                provider,
                object: null,
                gisResource: null,
                resource: null,
                data: null,
            },
        });
        this.handleStopDrawing();
    };

    handleObjectChange = (object: { model: string }) => {
        const gisResource = this.getResourceFromResourceName(DRAWING_RESOURCES[object.model].gisResourceName);

        let drawingObject = {
            object,
            gisResource,
            resource: DRAWING_RESOURCES[object.model].resource,
        };

        if (get(gisResource, 'type') === 'point') {
            drawingObject = { ...drawingObject, data: { points: [] } };
        } else {
            drawingObject = {
                ...drawingObject,
                data: {
                    points: [], lines: [], addedPoints: [], coordinates: [], linesCallback: null, pointCallback: null,
                },
            };
        }

        this.setState((prevState) => ({
            drawingObject: { ...prevState.drawingObject, ...drawingObject },
        }));
        this.handleStopDrawing();
    };

    getResourceFromResourceName = (resourceName: string): ?Resource => {
        const { resources } = this.props;
        return resources.find((resource) => resource.name === resourceName);
    };

    getFormDisplayValues = (formData: Object, formSchema: Object) => {
        const result = {};
        Object.keys(formData).forEach((fieldName) => {
            const field = formSchema.fields.find((schemaField) => schemaField[0] === fieldName)[1];
            if (field.type === 'choice') {
                result[fieldName] = field.ui.choices[formData[fieldName]];
            } else {
                result[fieldName] = formData[fieldName];
            }
        });
        return result;
    };

    handleStartDraw = async () => {
        const {
            editor, openPanel, validatePoint, context: contextMap,
        } = this.props;
        const { drawingObject } = this.state;
        const {
            provider, object, gisResource, resource,
        } = drawingObject;

        if (!provider || !object) {
            throw new Error('handleStartDraw should be called after an object is set');
        }

        const { model } = object;
        const { leaflet: { map } } = this.props;

        if (!map) return;

        // Getting model form data
        const formData = this.formikRef.current.getFormikBag().values;
        const formSchema = this.formikRef.current.props.schema;
        const formDisplayValues = this.getFormDisplayValues(formData, formSchema);
        this.formikRef.current.getFormikBag().setSubmitting(true);

        // PoP validation
        if (object.model === 'pop') {
            const res = await validatePoint(resource.name, contextMap.uuid, { oss_name: formData.oss_name });
            if (res.status === HTTP_400_BAD_REQUEST) {
                this.formikRef.current.getFormikBag().setFieldError('oss_name', res.data[0]);
                this.formikRef.current.getFormikBag().setSubmitting(false);
                return;
            }
        }

        // Open object drawing panel
        let panel;
        if (get(gisResource, 'type') === 'point') {
            map.on('pm:create', this.handleCreatedPoint);
            map.pm.Draw.Marker.toggle({
                markerStyle: {
                    icon: pointIcon(
                        model, false, model !== 'gispoint' ? provider.color : '#bdcc32',
                        this.formikRef.current.getFormikBag().values.underground_connection,
                    ),
                    draggable: false,
                },
                draggable: false,
                snappable: SNAPPABLE_MODELS.includes(model),
            });
            panel = POINT_FORM_KEY;
        } else if (model === 'cable') {
            panel = LINE_FORM_KEY;
        } else if (model === 'pipe') {
            panel = PIPE_FORM_KEY;
            editor.registerLoadingCallback(this.registerPointClickHandler());
            this.pipeWithForksDrawHandler();
        }
        this.setState({ drawingObject: { ...drawingObject, formData, formDisplayValues } });
        if (panel) {
            openPanel(panel);
        }
        if (drawingObject.object.model === LINE_CUT_MODEL) {
            // Make the icon of the LineCut disabled if it is snapped to a point
            map.pm.Draw.Marker._layer.on('pm:snap', ({ marker, snapLatLng }) => {
                const key = `${snapLatLng.lng}-${snapLatLng.lat}`;
                const point = get(map, ['coordsToLayers', key, 'point']);
                if (point) {
                    marker.setIcon(pointIcon(
                        model, true,
                    ));
                } else {
                    marker.setIcon(pointIcon(
                        model, false, model !== 'gispoint' ? provider.color : '#bdcc32',
                        this.formikRef.current.getFormikBag().values.underground_connection,
                    ));
                }
            });

            map.pm.Draw.Marker._layer.on('pm:unsnap', ({ marker }) => {
                marker.setIcon(pointIcon(
                    model, false, model !== 'gispoint' ? provider.color : '#bdcc32',
                    this.formikRef.current.getFormikBag().values.underground_connection,
                ));
            });
        }
        editor.setDrawing(true, true);
    };

    addToMap = (layer: Object) => {
        const { drawingFeatureGroup } = this.props;
        if (drawingFeatureGroup.current) {
            return layer.addTo(drawingFeatureGroup.current.leafletElement);
        }
        return null;
    };

    // Checks if a point is overlapping with another point
    checkOverlapping = (e: { layer: Object }) => {
        const { drawingObject } = this.state;
        const { data } = drawingObject;
        const { leaflet: { map } } = this.props;

        let isOverlapping = false;
        map.eachLayer((layer) => {
            if (get(layer, 'feature.geometry.type') === 'Point'
                && layer.getLatLng().equals(e.layer.getLatLng())) {
                isOverlapping = true;
            }
        });

        if (!isOverlapping) {
            data.points.forEach((point) => {
                if (point.getLatLng().equals(e.layer.getLatLng())) {
                    isOverlapping = true;
                }
            });
        }

        if (isOverlapping) {
            e.layer.remove();
        }
        return isOverlapping;
    };

    validateLineCut = (e) => {
        const { leaflet: { map } } = this.props;

        let valid = false;
        map.eachLayer((layer) => {
            if (get(layer, 'feature.geometry.type') === 'Point'
                && e.layer.getLatLng().equals(layer.getLatLng())) {
                valid = false;
            } else if (get(layer, 'feature.geometry.type') === 'LineString') {
                const latLngs = Object.values(layer.getLatLngs());
                if (isPointSnappedToLine(map, e.layer.getLatLng(), latLngs)) {
                    valid = true;
                }
            }
        });

        return valid;
    };

    handleCreatedPoint = (e: { layer: Object }) => {
        const { drawingObject, countCreatedObjects } = this.state;
        const { object, data, gisResource } = drawingObject;
        const { leaflet: { map } } = this.props;

        if (object.model === LINE_CUT_MODEL && !this.validateLineCut(e)) {
            e.layer.remove();
            return;
        }

        if (this.checkOverlapping(e)) return;

        // eslint-disable-next-line no-return-assign
        this.setState((prevState) => ({ countCreatedObjects: prevState.countCreatedObjects += 1 }));

        if (!gisResource) return;

        let number = data.points.length + 1;
        if (object.model === 'pop') {
            number = drawingObject.formData.alias || number;
        }
        const pointLayerOnMap = this.drawPoint(
            e.layer,
            object,
            number,
            drawingObject,
        );
        e.layer.remove();

        const newData: { points: Array<Object> } = { ...data };
        if (newData.points) {
            newData.points.push(pointLayerOnMap);
        } else {
            newData.points = [pointLayerOnMap];
        }

        this.setState({ drawingObject: { ...drawingObject, data: newData } });
        if (drawingObject.object.model === 'pop') {
            if (countCreatedObjects >= MAX_POINT_POP_COUNT) {
                map.pm.Draw.Marker.disable();
            }
        } else if (object.model === LINE_CUT_MODEL && countCreatedObjects >= MAX_POINT_LINE_CUT_COUNT) {
            map.pm.Draw.Marker.disable();
        } else if (countCreatedObjects >= MAX_POINT_OBJECTS_COUNT) {
            map.pm.Draw.Marker.disable();
        }
    };

    handleRemovePoint = (layer: Object) => {
        const { leaflet: { map } } = this.props;
        const { drawingObject, countCreatedObjects } = this.state;
        const { gisResource, data: { points } } = drawingObject;
        const { model } = drawingObject.object;
        layer.remove();
        points.splice(points.indexOf(layer), 1);
        // eslint-disable-next-line no-return-assign
        this.setState((prevState) => ({
            drawingObject: {
                ...drawingObject,
                data: { points },
            },
            countCreatedObjects: prevState.countCreatedObjects -= 1,
        }));

        if (countCreatedObjects <= MAX_POINT_OBJECTS_COUNT
            && get(gisResource, 'type') === 'point' && !map.pm.Draw.Marker.enabled()) {
            map.pm.Draw.Marker.enable({
                markerStyle: {
                    icon: pointIcon(
                        model, false, model !== 'gispoint' ? drawingObject.provider.color : '#bdcc32',
                        this.formikRef.current.getFormikBag().values.underground_connection,
                    ),
                    draggable: false,
                },
                draggable: false,
            });
        }
    };

    createdLineHighlightCallback = (coordinates: Array<[number, number]>,
        model: string, lineColors: Object) => {
        if (model === 'cable') {
            return useHighlightCable(coordinates, lineColors);
        }

        // Pipe
        return UseHighlightLine(coordinates, [{ weight: 16 }, { weight: 3 }], null, lineColors);
    };

    handleRemoveLastLinePoint = (layer: Object) => {
        const { drawingObject } = this.state;
        const {
            data: {
                points, lines,
            },
            object,
        } = drawingObject;

        let {
            data: {
                // eslint-disable-next-line prefer-const
                linesCallback, pointsCallback, coordinates, lineColors, addedPoints,
            },
        } = drawingObject;
        const { editor } = this.props;

        let newLineColors = { ...lineColors };
        let updatedAddedPoints = addedPoints.slice();

        if (addedPoints.includes(layer)) {
            updatedAddedPoints = updatedAddedPoints.filter((l) => l !== layer);
            layer.remove();
        } else {
            layer.on('click', this.handleCreatedLine);
        }
        points.splice(points.indexOf(layer), 1);

        editor.unregisterLoadingCallback(pointsCallback);
        if (points.length > 0) {
            const highlightCallback = pointsHighlightCallback(points, this.handleCreatedLine);
            editor.registerLoadingCallback(highlightCallback);
            pointsCallback = highlightCallback;
        }

        const removedLineLayer = lines[lines.length - 1];
        if (removedLineLayer) {
            const removedLineLayerCoords = removedLineLayer.toGeoJSON().geometry.coordinates;
            if (coordinates.length === removedLineLayerCoords.length) {
                coordinates = [];
                newLineColors = {};
            } else {
                newLineColors = omit(newLineColors, removedLineLayer.feature.id);
                coordinates.splice(
                    coordinates.length - (removedLineLayerCoords.length - 1),
                    removedLineLayerCoords.length - 1,
                );
            }

            editor.unregisterLoadingCallback(linesCallback);
            if (coordinates.length > 1) {
                const highlightCallback = this.createdLineHighlightCallback(coordinates, object.model, newLineColors);
                editor.registerLoadingCallback(highlightCallback);
                linesCallback = highlightCallback;
            }

            if (removedLineLayer.isNew) {
                removedLineLayer.remove();
            }
        }

        this.setState({
            drawingObject: {
                ...drawingObject,
                data: {
                    points,
                    lines: lines.slice(0, lines.length - 1),
                    coordinates,
                    linesCallback,
                    pointsCallback,
                    lineColors: newLineColors,
                    addedPoints: updatedAddedPoints,
                },
            },
        });

        editor.reloadFeatures();
    };

    _getProviderColor = async (lineId: UUID) => {
        const {
            linesToDetermineProvider, context: networkMap,
        } = this.props;
        const {
            drawingObject, drawingObject: { provider },
        } = this.state;

        const existingLines = await linesToDetermineProvider(networkMap.uuid, lineId);

        return determineProviderColor([...existingLines, {
            type: 'pipe',
            infrastructure_provider: provider.id,
            color: provider.color,
            diameter: drawingObject.modelData.diameter,
        }]);
    };


    handleCreatedLine = async (e: { target: Object }, snappedToLine: ?Object) => {
        const pointLayer = e.target;
        const { drawingObject } = this.state;
        const {
            data, gisResource, object, provider,
        } = drawingObject;

        if (!gisResource || !object || !data) return;
        const {
            points, lines, coordinates, linesCallback, pointsCallback, lineColors,
        } = data;
        const {
            leaflet: { map }, editor,
        } = this.props;

        pointLayer.closeTooltip();
        pointLayer.off('click', this.handleCreatedLine);

        const pointHighlightCallback = pointsHighlightCallback([...points, pointLayer], this.handleCreatedLine);

        if (isEmpty(points)) {
            this.setState((prevState) => ({
                drawingObject:
                    {
                        ...prevState.drawingObject,
                        data: {
                            ...prevState.drawingObject.data,
                            points: [pointLayer],
                            pointsCallback: pointHighlightCallback,
                        },
                    },
            }));
            editor.registerLoadingCallback(pointHighlightCallback);
            editor.reloadFeatures();
            return;
        }

        // If there is already a line between the two points find it and color it, otherwise draw straight line
        const lastPointLayer = points[points.length - 1];
        const startPointLines = get(map, ['coordsToLayers',
            pointLayer.toGeoJSON().geometry.coordinates.join('-'), 'lines']);
        const endPointLines = get(map, ['coordsToLayers', lastPointLayer.toGeoJSON().geometry.coordinates.join('-')])
            && get(map, ['coordsToLayers', lastPointLayer.toGeoJSON().geometry.coordinates.join('-'), 'lines']);

        let currLineLayer = null;
        if (!isEmpty(startPointLines) && !isEmpty(endPointLines)) {
            currLineLayer = startPointLines.find((line) => {
                const firstLineCoordinate = JSON.stringify(line.feature.geometry.coordinates[0]);
                const lastLineCoordinate = JSON.stringify(line.feature.geometry.coordinates
                    // eslint-disable-next-line no-unexpected-multiline
                    [line.feature.geometry.coordinates.length - 1]);
                const lastPointLayerCoordinate = JSON.stringify(lastPointLayer.toGeoJSON().geometry.coordinates);
                return (firstLineCoordinate === lastPointLayerCoordinate)
                    || (lastLineCoordinate === lastPointLayerCoordinate);
            });
        }

        let lineLayer = currLineLayer;
        let lineCoords = [lastPointLayer._latlng, pointLayer._latlng];

        // Ensures that broken gis lines are properly coloured when adding forks while drawing a pipe
        if ((snappedToLine
            && isPointSnappedToLine(map, points[points.length - 1].getLatLng(), snappedToLine.getLatLngs()))) {
            // the last drawn point is a fork and the fork lies on the same gis line as the previous point
            lineCoords = getSegmentBetweenPoints(map, lastPointLayer.getLatLng(), pointLayer.getLatLng(),
                snappedToLine.getLatLngs());
        } else if (lastPointLayer.isNew && startPointLines) {
            // the previous point is a fork, the currently selected point is an existing layer
            // and both lie on the same gis line
            startPointLines.forEach((layer) => {
                const latLngs = Object.values(layer.getLatLngs());
                const snappedToPreviousPoint = isPointSnappedToLine(map,
                    points[points.length - 1].getLatLng(), latLngs);
                if (snappedToPreviousPoint) {
                    lineCoords = getSegmentBetweenPoints(map, lastPointLayer.getLatLng(), pointLayer.getLatLng(),
                        layer.getLatLngs());
                }
            });
        }

        if (!lineLayer) {
            const { color } = provider;
            lineLayer = L.polyline(lineCoords, { color });
            lineLayer.isNew = true;
            lineLayer = this.addToMap(lineLayer);
            lineLayer.initialOptions = { color };
            lineLayer.feature = lineLayer.toGeoJSON();
            lineLayer.feature.id = uuidv4();
        }

        let lineCoordinates = lineLayer.toGeoJSON().geometry.coordinates.map((c) => [c[1], c[0]]);
        if (isEqual(lineLayer.toGeoJSON().geometry.coordinates[0],
            pointLayer.toGeoJSON().geometry.coordinates)) {
            lineCoordinates = lineCoordinates.reverse();
        }
        if (!isEmpty(coordinates)) {
            lineCoordinates = lineCoordinates.splice(1, lineCoordinates.length);
        }

        const lineData: LineData = {
            ...drawingObject.data,
            points: [...points, pointLayer],
            lines: [...lines, lineLayer],
            coordinates: [...coordinates, ...lineCoordinates],
            pointsCallback: pointHighlightCallback,
            lineColors: { ...lineColors },
        };

        // The provider color can change while drawing if the object we are creating is a pipe
        // as it depends on the biggest pipe on the route
        if (object.model === 'pipe') {
            if (!lineLayer.isNew) {
                // Drawing over an existing gis line -> use the existing pipes and the new one to determine the color
                lineData.lineColors[lineLayer.feature.id] = await this._getProviderColor(
                    lineLayer.feature.properties.identity,
                );
            } else if (snappedToLine && !snappedToLine.isNew) {
                // If the line layer is new, check if it was snapped to an existing gis line using a fork
                // Use the existing GIS line to determine the provider color of the new layer only if both the new point
                // and the previous point lie on the same gis line (otherwise use the provider color)
                const previousPoint = points[points.length - 1];
                const latLngs = Object.values(snappedToLine.getLatLngs());

                if (isPointSnappedToLine(map, previousPoint.getLatLng(), latLngs)) {
                    lineData.lineColors[lineLayer.feature.id] = await this._getProviderColor(
                        snappedToLine.feature.properties.identity,
                    );
                }
            } else if (lastPointLayer.isNew && startPointLines) {
                // Check if the previous point layer is new (a fork splitting an existing gis line) -- in this case
                // if both the last point and the previous point lie on the same gis line, use the provider color
                // of the previous line
                let existingRoute = null;
                startPointLines.forEach((layer) => {
                    const latLngs = Object.values(layer.getLatLngs());
                    const snappedToPreviousPoint = isPointSnappedToLine(map,
                        lastPointLayer.getLatLng(), latLngs);
                    if (snappedToPreviousPoint) {
                        existingRoute = layer;
                    }
                });

                if (existingRoute && !existingRoute.isNew) {
                    lineData.lineColors[lineLayer.feature.id] = await this._getProviderColor(
                        existingRoute.feature.properties.identity,
                    );
                }
            }
        }

        if (linesCallback) {
            editor.unregisterLoadingCallback(linesCallback);
        }

        const lineHighlightCallback = this.createdLineHighlightCallback(lineData.coordinates, object.model,
            lineData.lineColors);

        editor.registerLoadingCallback(lineHighlightCallback);
        lineData.linesCallback = lineHighlightCallback;

        if (pointsCallback) {
            editor.unregisterLoadingCallback(pointsCallback);
        }

        editor.registerLoadingCallback(pointHighlightCallback);

        // eslint-disable-next-line react/no-unused-state
        this.setState({ drawingObject: { ...drawingObject, data: lineData } },
            () => {
                editor.reloadFeatures();
            });
    };

    handleLineSelection = (line) => {
        // Find points
        // Add them to the callback
        //
    };

    drawPoint = (pointLayer, object, number, drawingObject) => {
        const newPointLayer = L.marker(pointLayer._latlng);
        newPointLayer.feature = {
            ...pointLayer.toGeoJSON(),
            properties: {
                ...pointLayer.toGeoJSON().properties,
                infrastructureProvider: drawingObject.provider.entity.name,
                point: {
                    color: drawingObject.provider.color,
                    child: {
                        underground_connection: drawingObject.formData.underground_connection,
                        type: object.model,
                    },
                },
                undergroundConnection: drawingObject.formData.underground_connection,
                objectType: object.model,
            },
        };
        newPointLayer.setIcon(numberedPointIcon(
            newPointLayer,
            number,
            true,
            undefined,
            object.model,
            drawingObject.provider.color,
            drawingObject.formData.underground_connection,
        ));
        pointLayer.remove();
        return this.addToMap(newPointLayer);
    };

    handleDrawFormChange = (e: Object) => {
        const { drawingObject } = this.state;

        if (this.formikRef.current) {
            this.formikRef.current.getFormikBag().validateForm().then(() => {
                this.setState({ canDraw: isEmpty(this.formikRef.current.getFormikBag().errors) });
            });
        } else {
            // If we're drawing a GIS Point, we don't have a formikRef since it's just the base object
            // and we have no form to validate, in this case we can always draw
            const canDrawObject = drawingObject.object.model === 'gispoint';

            this.setState({ canDraw: canDrawObject });
        }
    };

    handleStopDrawing = () => {
        const {
            leaflet: { map }, drawingFeatureGroup, editor,
        } = this.props;

        editor.unregisterAllLoadingCallback();
        if (map) {
            map.pm.Draw.disable();
            map.off('pm:create');
            map.off('pm:snap');
            map.off('pm:unsnap');
        }
        editor.setDrawing(false, false);

        const promise = editor._loadFeatures(true);
        if (promise) {
            promise.then(() => {
                if (drawingFeatureGroup.current) {
                    drawingFeatureGroup.current.leafletElement.clearLayers();
                }
            });
        }
    };

    handleSave = (data?: Object) => {
        const { drawingObject, submitting } = this.state;

        if (submitting) return;

        const {
            gisResource, object, provider, resource, data: drawingData,
        } = drawingObject;
        if (!provider || !gisResource || !object || !resource) return;

        const { createObject, context: map } = this.props;

        const formData = this.formikRef.current.getFormikBag().values;
        this.formikRef.current.setTouched(Object.keys(formData).reduce((touched, k) => {
            touched[k] = true;
            return touched;
        }, {}));

        this.setState({ submitting: true });
        if (object.model === 'pipe') {
            const { connectedPipes, exportEit } = data;
            const pipesData = drawingData.lines.map((line, idx) => {
                const { coordinates } = line.toGeoJSON().geometry;
                return {
                    start_point: {
                        type: 'Point',
                        coordinates: coordinates[0],
                    },
                    end_point: {
                        type: 'Point',
                        coordinates: coordinates[coordinates.length - 1],
                    },
                    continuous: connectedPipes.includes(idx),
                };
            });
            createObject(resource.name, map.uuid, {
                forks: drawingData.addedPoints.map((l) => ({
                    coordinates: l.feature.geometry,
                    infrastructure_provider: provider.uuid,
                })),
                infrastructure_provider: provider.uuid,
                [gisResource.geometryField]: pipesData,
                ...formData,
                export: exportEit,
            }).then((response) => {
                if (response.status !== HTTP_400_BAD_REQUEST) {
                    this.setState({ submitting: false });
                    this.handleClose(true);
                } else {
                    this.setState({
                        nonFieldErrData: response.data.non_field_errors
                            ? response.data.non_field_errors[0] : response.data[0],
                        closeMsgDialog: false,
                        submitting: false,
                    });
                }
            });
        } else {
            const pointsData = drawingData.points.map((point) => ({
                infrastructure_provider: provider.uuid,
                [gisResource.geometryField]: point.toGeoJSON().geometry,
                ...formData,
            }));
            createObject(resource.name, map.uuid, { points: pointsData }).then((response) => {
                this.setState({ submitting: false });
                this.setCreateObjectErrors(response, 'points');
            });
        }
    };

    handleSaveCable = (linesData: Object, reservesData: Object) => {
        const { drawingObject, submitting } = this.state;

        if (!drawingObject || submitting) return;

        const {
            provider, object, gisResource, resource,
        } = drawingObject;
        if (!provider || !object || !gisResource || !resource) return;

        const { createObject, context: map } = this.props;

        const formData = this.formikRef.current.getFormikBag().values;
        // Probably is because the button is disabled if the form is not valid
        this.formikRef.current.setTouched(Object.keys(formData).reduce((touched, k) => {
            touched[k] = true;
            return touched;
        }, {}));

        this.setState({ submitting: true });
        createObject(resource.name, map.uuid, {
            infrastructure_provider: provider.id,
            ...formData,
            [gisResource.geometryField]: linesData,
            reserves: reservesData,
        }).then((response) => {
            this.setState({ submitting: false });
            this.setCreateObjectErrors(response);
        });
    };

    handleClose = (skip = false) => {
        const { onClose } = this.props;
        if (onClose) {
            onClose(skip);
        }
    };

    setCloseMsgDialog = (close = true) => {
        this.setState({ closeMsgDialog: close });
    };

    setCreateObjectErrors = (response, errorAccessor = null) => {
        const { nonFieldErrData } = this.state;
        if (response.status === HTTP_400_BAD_REQUEST) {
            const formik = this.formikRef.current;
            const nonFieldErrors = errorAccessor ? response.data[errorAccessor][0].non_field_errors
                : response.data.non_field_errors;
            this.setState({
                nonFieldErrData: nonFieldErrors,
                closeMsgDialog: false,
            });
            formik.setErrors(nonFieldErrData || response.data);
        } else {
            this.setState({ nonFieldErrData: null, closeMsgDialog: true }, () => {
                this.handleClose(true);
            });
        }
    };

    skipEventCloseHandler = (skip) => {
        this.handleClose(skip);
    };

    updateDrawingObjectData = (objectData) => {
        this.setState((prevState) => ({
            ...prevState,
            drawingObject: {
                ...prevState.drawingObject,
                data: {
                    ...prevState.drawingObject.data,
                    ...objectData,
                },
            },
        }));
    };

    render() {
        const {
            context, drawing, intl: { formatMessage }, editor, id, leaflet: { map },
        } = this.props;
        const {
            drawingObject, nonFieldErrData, closeMsgDialog, submitting,
        } = this.state;
        const { resource } = drawingObject;

        return (
            <>
                <Creator
                    id={id}
                    title={(
                        <FormattedMessage
                            id="networkcreator-title"
                            defaultMessage="Infrastructure Object Configurator"
                        />
                    )}
                    onClose={(e) => this.handleClose()}
                    onSave={this.handleSave}
                    onStartDraw={this.handleStartDraw}
                    isDrawing={drawing}
                    canSave={!!drawingObject.data}
                    formikRef={this.formikRef}
                >
                    <NetworkDrawForm
                        forwardRef={this.formikRef}
                        onProviderChange={this.handleProviderChange}
                        onObjectChange={this.handleObjectChange}
                        onObjectFormChange={this.handleDrawFormChange}
                        map={context}
                        resource={resource}
                        onLoadModelData={this.handleLoadModelData}
                    />
                </Creator>
                {(drawingObject) && (
                    <SidePane
                        id={LINE_FORM_KEY}
                        title={(
                            <FormattedMessage
                                id="networklineform-title"
                                defaultMessage="Infrastructure Cabel - Pipe configurator"
                            />
                        )}
                    >
                        <CreateCable
                            formData={drawingObject.formData}
                            provider={drawingObject.provider}
                            object={drawingObject.object}
                            formDisplayValues={drawingObject.formDisplayValues}
                            onSubmit={this.handleSaveCable}
                            onClose={this.skipEventCloseHandler}
                        />
                    </SidePane>
                )}
                {(drawingObject && drawingObject.data) && (
                    <SidePane
                        id={PIPE_FORM_KEY}
                        title={(
                            <FormattedMessage
                                id="network-pipe-form-title"
                                defaultMessage="Infrastructure Pipe draw configurator"
                            />
                        )}
                    >
                        <PipeForm
                            // $FlowFixMe
                            drawingObject={drawingObject}
                            onLastRemove={this.handleRemoveLastLinePoint}
                            onSubmit={this.handleSave}
                            onClose={(e) => this.handleClose()}
                        />
                    </SidePane>
                )}
                {(drawingObject && drawingObject.data) && (
                    <SidePane
                        id={POINT_FORM_KEY}
                        title={(
                            <FormattedMessage
                                id="networkpointform-title"
                                defaultMessage="Infrastructure Point draw configurator"
                            />
                        )}
                    >
                        <PointForm
                            drawingObject={drawingObject}
                            onPointRemove={this.handleRemovePoint}
                            onSubmit={this.handleSave}
                            onClose={(e) => this.handleClose()}
                        />
                    </SidePane>
                )}
                {(map && map.getZoom() < POINTS_MIN_ZOOM) && (
                    <SidePane
                        id="zoom-restrictor"
                        openInitially
                    >
                        <Box justify="between" height="100%">
                            <Box pad="medium" height="100%" justify="center">
                                {formatMessage(messages.restrictZoom, { minZoom: POINTS_MIN_ZOOM })}
                            </Box>
                            <ActionButtons onClose={(e) => this.handleClose()} onSave={false} />
                        </Box>
                    </SidePane>
                )}
                {(!closeMsgDialog && nonFieldErrData) && (
                    <MessageDialog
                        message={nonFieldErrData}
                        title={formatMessage(messages.errorTitle)}
                        key={messages.errorTitle.id}
                        actions={{
                            cancel: {
                                label: formatMessage(messages.error),
                                callback: () => this.setCloseMsgDialog(),
                                type: 'negative',
                            },
                        }}
                    />
                )}
                <Spinner loading={submitting} />
            </>
        );
    }
}

export default injectIntl(NetworkCreator);
